import styled from '@xstyled/styled-components';
import React, { useState } from 'react';
import { t } from 'ttag';
import ChevronIcon from '~/assets/icons/chevron.svg';
import MoonIcon from '~/assets/icons/moon.svg';
import { select } from '~/lib/select';
import { languageName } from '~/lib/translations/languageName';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Capsule, LanguageCapsule, PlaybackSpeedCapsule } from '~/ui/capsules';
import { Flex } from '~/ui/flex/Flex';
import { DarkThemeLogo, Logo } from '~/ui/logo/Logo';
import { ToggleSwitch } from '~/ui/toggle/ToggleSwitch';
import { TrainingState, useTrainingStore } from '../state';
import { HeaderBlock } from './Header';
import { formatSpeed } from './playback-speed/playbackSpeeds';
import { SettingsPages, useSettingsStore } from './Store';
import { useToggleAudioEffect } from '~/ui/audio-effect/useToggleAudioEffect';

const MenuItem = styled.buttonBox`
  border: none;
  background: none;
  display: flex;
  align-items: center;
  min-width: 230px;
  padding: 0;
  color: ${p => p.theme.colors.black};
  & + & {
    margin-top: 20px;
  }
`;
MenuItem.defaultProps = {
  fontSize: { _: 'md', sm: 'sm' }
};

const MenuItemBox = styled.divBox`
  border: none;
  background: none;
  display: flex;
  align-items: center;
  min-width: 230px;
  padding: 0;
  color: ${p => p.theme.colors.black};
  margin: 20px 0;
`;
MenuItemBox.defaultProps = {
  fontSize: { _: 'md', sm: 'sm' }
};

const MenuItemLabel = styled.span`
  margin-left: 15px;
  flex: 1;
  text-align: left;
`;

const Chevron = styled(ChevronIcon)`
  margin-left: 8px;
  stroke: var(--color-black);
`;

const HeaderTitle = styled.div`
  font-family: Recoleta;
  font-size: lg;
  color: black;
  width: 100%;
  text-align: center;
  margin-top: 3px;
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'textLocale',
    'mediumLocale',
    'playbackSpeed',
    'subtitles',
    'setSubtitles',
    'logoUrl',
    'companyId',
    'auditoryFeedback',
    'setAuditoryFeedback',
    'visualFeedback',
    'setVisualFeedback',
    'darkMode',
    'setDarkMode',
    'setAutoPlay',
    'autoPlay',
    'subtitleLocale'
  );

export const Root = ({ headerTitle }: { headerTitle?: string }) => {
  const {
    textLocale,
    mediumLocale,
    playbackSpeed,
    subtitles,
    setSubtitles,
    logoUrl,
    companyId,
    auditoryFeedback,
    setAuditoryFeedback,
    visualFeedback,
    setVisualFeedback,
    darkMode,
    autoPlay,
    setAutoPlay,
    subtitleLocale
  } = useTrainingStore(selector);

  const { page, setPage, toggle } = useSettingsStore();
  const [caption, toggleCaption] = useState<boolean>(subtitles);
  const [audioFeedback, toggleAudioFeedback] =
    useState<boolean>(auditoryFeedback);
  const [visualEffects, toggleVisualEffects] =
    useState<boolean>(visualFeedback);
  const [_autoPlay, toggleAutoPlay] = useState<boolean>(autoPlay || false);

  const [playAudioEffect] = useToggleAudioEffect();

  if (page !== SettingsPages.Root) return null;

  let showDarkLogo = false;

  if (
    darkMode === 'dark' ||
    (darkMode === 'automatic' &&
      window?.matchMedia('(prefers-color-scheme: dark)')?.matches)
  ) {
    showDarkLogo = true;
  }

  const getHeader = () => {
    if (headerTitle) {
      return <HeaderTitle>{headerTitle}</HeaderTitle>;
    } else if (companyId == '3983') {
      return <Logo src={logoUrl} height={36} title="Easyllama" />;
    } else if (showDarkLogo) {
      return <DarkThemeLogo height={36} title="Easyllama" />;
    } else {
      return <Logo height={36} title="Easyllama" />;
    }
  };

  return (
    <>
      <CloseButton onClick={() => toggle(false)} />
      <Flex variant="list" className="modalStyleForDarkTheme">
        <HeaderBlock>{getHeader()}</HeaderBlock>
        <MenuItem onClick={() => setPage(SettingsPages.MediumLocale)}>
          <LanguageCapsule highlighted />
          <MenuItemLabel>{t`Audio:`}</MenuItemLabel>
          <span>{languageName(mediumLocale)}</span>
          <Chevron />
        </MenuItem>
        <MenuItem onClick={() => setPage(SettingsPages.TextLocale)}>
          <LanguageCapsule highlighted />
          <MenuItemLabel>{t`On-Screen Text:`}</MenuItemLabel>
          <span>{languageName(textLocale)}</span>
          <Chevron />
        </MenuItem>
        <MenuItem onClick={() => setPage(SettingsPages.SubtitleLocale)}>
          <LanguageCapsule highlighted />
          <MenuItemLabel>{t`Subtitles:`}</MenuItemLabel>
          <span>{languageName(subtitleLocale)}</span>
          <Chevron />
        </MenuItem>
        <MenuItemBox
          onKeyPress={e => {
            if (e.key === 'Enter') {
              audioFeedback &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              toggleCaption(!caption);
              setSubtitles(!caption);
            }
          }}
        >
          <Capsule highlighted fontWeight="bold">
            cc
          </Capsule>
          <MenuItemLabel id="captions">
            <label htmlFor="cc">{t`Subtitles:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="cc"
            name="cc"
            value={subtitles ? t`On` : t`Off`}
            checked={caption}
            onChange={() => {
              toggleCaption(!caption);
              setSubtitles(!caption);
            }}
            showLabel={false}
          />
        </MenuItemBox>
        <MenuItem onClick={() => setPage(SettingsPages.Speed)}>
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel>{t`Playback Speed:`}</MenuItemLabel>
          <span>{formatSpeed(playbackSpeed)}</span>
          <Chevron />
        </MenuItem>
        <MenuItem onClick={() => setPage(SettingsPages.DarkMode)}>
          <Capsule highlighted fontWeight="bold">
            <MoonIcon />
          </Capsule>
          <MenuItemLabel>{t`Theme:`}</MenuItemLabel>
          <Chevron />
        </MenuItem>
        <MenuItemBox
          style={{ marginBottom: '0px' }}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              audioFeedback &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              setAuditoryFeedback(!audioFeedback);
              toggleAudioFeedback(!audioFeedback);
            }
          }}
        >
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel id="sound effects">
            <label htmlFor="auditoryFeedback">{t`Sound Effects:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="auditoryFeedback"
            name="auditoryFeedback"
            value={audioFeedback ? t`On` : t`Off`}
            checked={audioFeedback}
            onChange={() => {
              setAuditoryFeedback(!audioFeedback);
              toggleAudioFeedback(!audioFeedback);
            }}
            showLabel={false}
          />
        </MenuItemBox>
        <MenuItemBox
          style={{ marginBottom: '0px' }}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              audioFeedback &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              setVisualFeedback(!visualEffects);
              toggleVisualEffects(!visualEffects);
            }
          }}
        >
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel id="visual effects">
            <label htmlFor="visualFeedback">{t`Animations:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="visualFeedback"
            name="visualFeedback"
            value={visualEffects ? t`On` : t`Off`}
            checked={visualEffects}
            onChange={() => {
              setVisualFeedback(!visualEffects);
              toggleVisualEffects(!visualEffects);
            }}
            showLabel={false}
          />
        </MenuItemBox>
        <MenuItemBox
          onKeyPress={e => {
            if (e.key === 'Enter') {
              _autoPlay &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              setAutoPlay(!_autoPlay);
              toggleAutoPlay(!_autoPlay);
            }
          }}
        >
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel id="auto play">
            <label htmlFor="autoPlay">{t`Auto Play:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="autoPlay"
            name="autoPlay"
            value={_autoPlay ? t`On` : t`Off`}
            checked={_autoPlay}
            onChange={() => {
              setAutoPlay(!_autoPlay);
              toggleAutoPlay(!_autoPlay);
            }}
            showLabel={false}
          />
        </MenuItemBox>
      </Flex>
    </>
  );
};
